import { useState } from "react";
import { Check, Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const languages = [
  { code: "EN", label: "English", native: "English" },
  { code: "FR", label: "French", native: "Français" },
  { code: "ZH", label: "Chinese", native: "中文" },
  { code: "VI", label: "Vietnamese", native: "Tiếng Việt" },
  { code: "DE", label: "German", native: "Deutsch" },
  { code: "JA", label: "Japanese", native: "日本語" },
];

const Nav = () => {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("EN");
  const current = languages.find((l) => l.code === lang) ?? languages[0];

  const links = [
    { href: "/tours", label: "Cambodia Tours" },
    { href: "/#story", label: "Our Story" },
    { href: "/blog", label: "Blog" },
    { href: "/#gallery", label: "Gallery" },
    { href: "/#contact", label: "Contact" },
  ];

  const LanguageMenu = ({ className = "" }: { className?: string }) => (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={`inline-flex items-center gap-2 border border-background/40 text-background text-[11px] uppercase tracking-[0.25em] px-3 py-2.5 hover:bg-background/10 focus:outline-none focus:ring-1 focus:ring-background/60 transition-colors ${className}`}
        aria-label="Select language"
      >
        <Globe className="h-3.5 w-3.5 opacity-80" />
        <span>{current.code}</span>
        <span className="opacity-60 normal-case tracking-normal text-[11px]">{current.native}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[12rem] bg-foreground text-background border-background/10">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.3em] text-background/80 font-normal">
          Language
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-background/10" />
        {languages.map((l) => {
          const active = l.code === lang;
          return (
            <DropdownMenuItem
              key={l.code}
              onSelect={() => setLang(l.code)}
              className={`flex items-center justify-between gap-3 cursor-pointer focus:bg-background/10 focus:text-background ${
                active ? "bg-background/10 text-gold" : "text-background"
              }`}
            >
              <span className="flex items-center gap-3">
                <span className="text-[10px] uppercase tracking-[0.25em] opacity-70 w-6">{l.code}</span>
                <span className="flex flex-col leading-tight">
                  <span className="text-sm">{l.native}</span>
                  <span className="text-[10px] uppercase tracking-[0.25em] opacity-50">{l.label}</span>
                </span>
              </span>
              {active && <Check className="h-3.5 w-3.5 text-gold" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-30 bg-foreground/30 backdrop-blur-md supports-[backdrop-filter]:bg-foreground/20 border-b border-background/10">
      <div className="container flex items-center justify-between py-6">
        <a href="/" className="flex items-center gap-2 text-background">
          <span className="font-display text-2xl tracking-wide">Aṅkor</span>
          <span className="text-[10px] uppercase tracking-[0.3em] opacity-70">Sojourns</span>
        </a>

        <nav className="hidden md:flex items-center gap-10 text-xs uppercase tracking-[0.25em] text-background/90">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="hover:text-background transition-colors">
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <LanguageMenu />
          <a href="#contact" className="inline-block text-xs uppercase tracking-[0.25em] border border-background/40 text-background px-5 py-2.5 hover:bg-background hover:text-foreground transition-colors">
            Plan a Journey
          </a>
        </div>

        <button
          type="button"
          aria-label="Toggle menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="md:hidden flex flex-col justify-center items-center w-11 h-11 text-background"
        >
          <span className={`block w-6 h-px bg-background transition-transform ${open ? "translate-y-[5px] rotate-45" : ""}`} />
          <span className={`block w-6 h-px bg-background mt-1 transition-opacity ${open ? "opacity-0" : ""}`} />
          <span className={`block w-6 h-px bg-background mt-1 transition-transform ${open ? "-translate-y-[5px] -rotate-45" : ""}`} />
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-foreground/95 backdrop-blur">
          <nav className="container flex flex-col py-6 gap-5 text-xs uppercase tracking-[0.25em] text-background">
            {links.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="py-2 border-b border-background/10">
                {l.label}
              </a>
            ))}
            <div className="pt-2">
              <LanguageMenu />
            </div>
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              className="mt-2 inline-block text-center border border-background/40 px-5 py-3 hover:bg-background hover:text-foreground transition-colors"
            >
              Plan a Journey
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
export default Nav;
